import {ChangeDetectionStrategy, Component, OnInit} from '@angular/core';
import { CommonModule } from "@angular/common";
import { MatButtonModule } from "@angular/material/button";
import { MatCardModule } from "@angular/material/card";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatIconModule } from "@angular/material/icon";
import { MatOptionModule } from "@angular/material/core";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { MatSelectModule } from "@angular/material/select";
import { MatTooltipModule } from "@angular/material/tooltip";
import { Store } from "@ngxs/store";
import { AppState } from "../../../state";
import { Observable } from "rxjs";
import { Language, Product, Version } from "../../../services/api";
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { PermissionService, DownloadService } from "../../../services";
import {
    ProductApiStatusState,
    ClearJiraIssue,
    CheckStatusEnum,
    ExportJiraTranslationProduct,
    ExportJiraTranslationVersion,
    GetJiraIssue,
    ClearCheckNote,
    ClearCreatedNote,
    ClearExported
} from "../../product/state";
import { MatInputModule } from "@angular/material/input";

import { MatDividerModule } from "@angular/material/divider";
import { SubscriptionHandlerComponent } from "../subscription-handler/subscription-handler.component";
import { MatDialogModule } from "@angular/material/dialog";

@Component({
    selector: 'app-export-dialog',
    imports: [
        CommonModule,
        MatButtonModule,
        MatCardModule,
        MatDialogModule,
        MatFormFieldModule,
        MatIconModule,
        MatOptionModule,
        MatProgressSpinnerModule,
        MatSelectModule,
        MatTooltipModule,
        ReactiveFormsModule,
        MatInputModule,
        MatDividerModule
    ],
    templateUrl: './export-dialog.component.html',
    styleUrl: './export-dialog.component.css',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ExportDialogComponent extends SubscriptionHandlerComponent implements OnInit {
    languages$: Observable<Language[]> = this.store.select(AppState.languages);

    checkedNote$: Observable<CheckStatusEnum | null> = this.store.select(ProductApiStatusState.checkNote);

    exportedTranslation$: Observable<boolean | null> = this.store.select(ProductApiStatusState.exportedTranslation);

    product: Product | null = null;

    version: Version | null = null;

    // Export button pressed flag
    exportPressed: boolean = false;

    // Issue Key of the checked note
    checkedIssueKey: string = "";

    // Loading animation flag
    loading = false;

    selectedLanguage = new FormControl<Language | null>(null);

    check: FormGroup = new FormGroup({
        issueKey: new FormControl<string>("",  {validators: Validators.required, nonNullable: true}),
    });

    constructor(public permissionService: PermissionService,
                private store: Store,
                private downloadService: DownloadService) {
        super();
    }

    ngOnInit() {
        this.store.dispatch(new ClearExported())
        this.store.dispatch(new ClearCheckNote());

        this.subscribeTo(this.languages$, languages => {
            if (languages) {
                // Filter out Developer-Language for export
                let availableLanguages = languages.filter(x => x.id !== 1)
                if (availableLanguages.length > 0)
                    this.selectedLanguage.setValue(availableLanguages[0])
            }
        })
    }

    // Jira Key input changed
    inputChanged() {
        this.loading = false;
        this.store.dispatch(new ClearCheckNote());
    }

    // Enter functionality for checking Type-Value
    enterCheck() {
        this.check.controls['issueKey'].markAsTouched();
        this.checkNote();
    }

    // Check if Jira Key valid
    checkNote() {
        this.loading = true;
        this.store.dispatch(new ClearCheckNote());
        this.store.dispatch(new ClearCreatedNote());
        this.checkedIssueKey = this.check.value.issueKey;
        this.store.dispatch(new ClearJiraIssue());
        this.store.dispatch(new GetJiraIssue(this.checkedIssueKey));
    }

    // Export the translation to file
    exportFile() {
        if (!this.selectedLanguage.value) {
            return;
        }

        if (this.version) {
            this.downloadService.downloadVersionTranslationXml(this.version, this.selectedLanguage.value).then();
        } else if (this.product) {
            this.downloadService.downloadProductTranslationXml(this.product, this.selectedLanguage.value).then();
        }
    }

    // Export the translation to Jira
    exportJira() {
        if (!this.selectedLanguage.value) {
            return;
        }

        const languageId = this.selectedLanguage.value.id ?? 0;

        this.exportPressed = true;
        this.store.dispatch(new ClearExported())
        if (this.version) {
            this.store.dispatch(new ExportJiraTranslationVersion(this.checkedIssueKey, this.version.id!, languageId));
        } else if (this.product) {
            this.store.dispatch(new ExportJiraTranslationProduct(this.checkedIssueKey, this.product.id!, languageId));
        }
    }

    protected readonly CheckStatus = CheckStatusEnum;
}
